import {BOX_HEIGHTS, BOX_WIDTHS} from '@hconnect/uikit'
import {PageTitle} from '@hconnect/uikit/src/lib2'
import {Stack, useMediaQuery, useTheme} from '@mui/material'
import React from 'react'

import {
  OperationHoursTimeRangeContainer,
  EventsContainer,
  CommentsContainer,
  ShiftTasksContainer,
  OptimizationSpecialistKpisContainer,
  OptimizationSpecialistFilterContainer,
  OptimizationSpecialistExpertSystemContainer,
  OptimizationSpecialistMaterialQualityKpisContainer,
  PlantConfigContainerWrapper
} from '../containers'
import {PlantDashboardLayout} from '../layouts'
const kpiMobileProps = {
  cardBoxSx: {minWidth: {xs: '100%'}},
  placeholderWrapperSx: {width: {xs: '100%'}},
  gridItemXs: 6
}

const kpiDesktopProps = {
  cardBoxSx: {
    height: {md: BOX_HEIGHTS['L']},
    minWidth: {xs: '100%', lg: BOX_WIDTHS['XL']}
  },
  placeholderWrapperSx: {
    height: {md: '100%'},
    minWidth: {xs: '100%', lg: BOX_WIDTHS['XL']}
  },
  gridItemXs: 3
}

type Props = {
  plantId: string
  titleSlot?: React.ReactNode
  aboveTitleSlot?: React.ReactNode
  cockpitHref?: string
  optimizationSpecialistPerformanceHref?: string
  onEquipmentClick?: (equipmentId: string) => void
}

export const OptimizationSpecialistPlantDashboard = ({
  plantId,
  titleSlot,
  aboveTitleSlot,
  cockpitHref,
  optimizationSpecialistPerformanceHref,
  onEquipmentClick
}: Props) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const isTablet = !useMediaQuery(theme.breakpoints.down('sm'))
  const isLarge = !useMediaQuery(theme.breakpoints.down('lg'))

  const showInOneColumnWithoutLegend = useMediaQuery(theme.breakpoints.not('md'))

  const titleSlotStack = (
    <Stack>
      {aboveTitleSlot}
      <PageTitle ml={0}>{titleSlot}</PageTitle>
    </Stack>
  )

  return (
    <PlantConfigContainerWrapper plantId={plantId}>
      <PlantDashboardLayout
        titleSlot={titleSlotStack}
        headerActionContent={<OptimizationSpecialistFilterContainer plantId={plantId} />}
        plantStatusSlot={
          <OptimizationSpecialistKpisContainer
            plantId={plantId}
            isMobile={isMobile}
            {...(isMobile ? kpiMobileProps : kpiDesktopProps)}
            optimizationSpecialistPerformanceHref={optimizationSpecialistPerformanceHref}
          />
        }
        commentsSlot={
          <CommentsContainer
            plantId={plantId}
            cardBoxSx={{height: BOX_HEIGHTS['L'], width: {xs: '100%', lg: BOX_WIDTHS['L']}}}
            placeholderWrapperSx={{width: {xs: '100%', lg: BOX_WIDTHS['L']}}}
          />
        }
        expertSystemUsageSlot={
          <OptimizationSpecialistExpertSystemContainer
            plantId={plantId}
            cardBoxSx={{
              width: '100%',
              height: BOX_HEIGHTS['M']
            }}
            cockpitHref={cockpitHref}
            placeholderWrapperSx={{height: '100%'}}
          />
        }
        operationHoursSlot={
          <OperationHoursTimeRangeContainer
            plantId={plantId}
            onEquipmentClick={onEquipmentClick}
            cardBoxSx={{height: {sm: BOX_HEIGHTS['M']}, width: {xs: '100%', sm: BOX_WIDTHS['S']}}}
            isMobile={showInOneColumnWithoutLegend}
            hideLegend={showInOneColumnWithoutLegend}
          />
        }
        eventsSlot={
          <EventsContainer
            plantId={plantId}
            cockpitHref={cockpitHref}
            cardBoxSx={{height: BOX_HEIGHTS['S'], width: {xs: '100%', sm: BOX_WIDTHS['S']}}}
            placeholderWrapperSx={{height: '100%', width: {xs: '100%', sm: BOX_WIDTHS['S']}}}
          />
        }
        shiftTasksSlot={
          <ShiftTasksContainer
            plantId={plantId}
            cardBoxSx={{height: BOX_HEIGHTS['S'], width: {xs: '100%', sm: BOX_WIDTHS['S']}}}
            placeholderWrapperSx={{height: '100%', width: {xs: '100%', sm: BOX_WIDTHS['S']}}}
          />
        }
        qualityParametersSlot={
          <OptimizationSpecialistMaterialQualityKpisContainer
            plantId={plantId}
            cardBoxSx={{
              width: '100%',
              height: BOX_HEIGHTS['M']
            }}
            placeholderWrapperSx={{height: '100%'}}
          />
        }
        isMobile={isMobile}
        isTablet={isTablet}
        isLarge={isLarge}
      />
    </PlantConfigContainerWrapper>
  )
}
